import { putUserSetting } from "@/js/api.schema";
import {
  dashboardGroups,
  dashboardTableHeadersPatients,
  dashboardTableHeadersStudies
} from "@/js/preferences";

const COLUMN_PREFS_VERSION = 1;
const GLOBAL_MIN_WIDTH = 60;
const GLOBAL_MAX_WIDTH = 600;
const ACTIONS_FIXED_WIDTH = 120;

const BASE_HEADERS_BY_VIEW = {
  patients: dashboardTableHeadersPatients,
  studies: dashboardTableHeadersStudies
};

const DEFAULT_WIDTHS_BY_VIEW = {
  patients: {
    custom_tag: 140,
    name: 220,
    patient_id: 140,
    gender: 100,
    birth_date: 120,
    annotation_modified_at: 160,
    anonymized: 90,
    has_patient_consent: 120,
    studies: 100,
    size_bytes: 110,
    modalities: 120,
    running_async_tasks: 110,
    owner: 140
  },
  studies: {
    custom_tag: 140,
    patient: 220,
    has_masks: 90,
    accession_number: 160,
    study_description: 240,
    study_date: 120,
    annotation_modified_at: 160,
    gt_diagnosis_status: 90,
    number_of_files: 110,
    size_bytes: 110,
    modalities: 120,
    running_async_tasks: 110,
    number_of_surfaces: 110,
    owner: 140
  }
};

const COLUMN_ALIASES = {
  patients: {},
  studies: {}
};

// Demo accounts always start from these column defaults at login: the demo
// user is shared, so per-visitor saved prefs must not leak between sessions.
const DEMO_VISIBILITY_OVERRIDES = {
  studies: {
    accession_number: false,
    number_of_files: true
  }
};

const getDemoDefaultRawPrefs = () => ({
  studies: { visibility: { ...DEMO_VISIBILITY_OVERRIDES.studies } }
});

const MANDATORY_MIN_WIDTH_BY_VIEW = {
  patients: {
    custom_tag: 90,
    name: 140
  },
  studies: {
    custom_tag: 90,
    patient: 140
  }
};

const initialFilter = {
  patients: {
    patientName: "",
    patientId: "",
    birthDate: "",
    gender: "",
    customTag: ""
  },
  studies: {
    accessionNumber: "",
    studyDate: "",
    modality: "",
    patientName: "",
    customTag: ""
  }
};

const getInitialSearchMeta = () => ({
  mode: "",
  parsed: { tokens: [], warnings: [], free_text_terms: [] },
  warnings: [],
  suggestions: { prefixes: [] },
  counts: { patients: 0, studies: 0, items: 0 }
});

const isPlainObject = value =>
  Boolean(value) && typeof value === "object" && !Array.isArray(value);

const isKnownView = view =>
  Object.prototype.hasOwnProperty.call(BASE_HEADERS_BY_VIEW, view);

const getBaseHeadersForView = view => BASE_HEADERS_BY_VIEW[view] || [];

const getHeaderByColumn = (view, colValue) =>
  getBaseHeadersForView(view).find(header => header.value === colValue);

const hasKnownColumn = (view, colValue) => Boolean(getHeaderByColumn(view, colValue));

const resolveColumnAlias = (view, colValue) => {
  const aliasMap = COLUMN_ALIASES[view] || {};
  return aliasMap[colValue] || colValue;
};

const isMandatoryColumn = (view, colValue) => {
  const header = getHeaderByColumn(view, colValue);
  if (!header) return false;
  return header.mandatory === true || colValue === "actions";
};

const isResizableColumn = (view, colValue) => {
  if (!hasKnownColumn(view, colValue)) return false;
  return colValue !== "actions";
};

const getMinWidthForColumn = (view, colValue) => {
  if (colValue === "actions") {
    return ACTIONS_FIXED_WIDTH;
  }
  const viewPolicy = MANDATORY_MIN_WIDTH_BY_VIEW[view] || {};
  return viewPolicy[colValue] || GLOBAL_MIN_WIDTH;
};

const getDefaultWidthForColumn = (view, colValue) => {
  const viewDefaults = DEFAULT_WIDTHS_BY_VIEW[view] || {};
  return viewDefaults[colValue];
};

const clampColumnWidth = (view, colValue, width) => {
  const numeric = Number(width);
  if (!Number.isFinite(numeric)) return undefined;
  const minWidth = Math.max(GLOBAL_MIN_WIDTH, getMinWidthForColumn(view, colValue));
  return Math.min(GLOBAL_MAX_WIDTH, Math.max(minWidth, Math.round(numeric)));
};

const getDefaultVisibilityForHeader = header => {
  if (!header || !header.value) return false;
  if (header.value === "actions" || header.mandatory === true) return true;
  return !header.hidden;
};

const getDefaultOrderForView = view =>
  getBaseHeadersForView(view)
    .map(header => header.value)
    .filter(Boolean);

const getDefaultViewPrefs = view => {
  const visibility = {};
  const widths = {};

  getBaseHeadersForView(view).forEach(header => {
    visibility[header.value] = getDefaultVisibilityForHeader(header);
    const defaultWidth = getDefaultWidthForColumn(view, header.value);
    if (defaultWidth !== undefined && isResizableColumn(view, header.value)) {
      const clamped = clampColumnWidth(view, header.value, defaultWidth);
      if (clamped !== undefined) {
        widths[header.value] = clamped;
      }
    }
  });

  if (hasKnownColumn(view, "actions")) {
    visibility.actions = true;
    widths.actions = ACTIONS_FIXED_WIDTH;
  }

  return {
    visibility,
    widths,
    order: getDefaultOrderForView(view)
  };
};

const migrateColumnPrefsToSupportedShape = rawPrefs => {
  if (!isPlainObject(rawPrefs)) return {};

  // Legacy payload v0 (no explicit version): same shape as current payload.
  if (rawPrefs.version === undefined || rawPrefs.version === COLUMN_PREFS_VERSION) {
    return rawPrefs;
  }

  // Unknown future/old versions: best-effort extraction, otherwise fallback to defaults.
  const migrated = {};
  if (isPlainObject(rawPrefs.patients)) {
    migrated.patients = rawPrefs.patients;
  }
  if (isPlainObject(rawPrefs.studies)) {
    migrated.studies = rawPrefs.studies;
  }
  return migrated;
};

const normalizeViewPrefs = (view, rawViewPrefs) => {
  const defaults = getDefaultViewPrefs(view);
  const result = {
    visibility: { ...defaults.visibility },
    widths: {},
    order: [...defaults.order]
  };

  if (!isPlainObject(rawViewPrefs)) {
    result.widths = { ...defaults.widths };
    return result;
  }

  const rawVisibility = isPlainObject(rawViewPrefs.visibility)
    ? rawViewPrefs.visibility
    : {};
  Object.entries(rawVisibility).forEach(([rawKey, rawVisible]) => {
    const colValue = resolveColumnAlias(view, rawKey);
    if (!hasKnownColumn(view, colValue)) return;
    result.visibility[colValue] = Boolean(rawVisible);
  });

  const rawWidths = isPlainObject(rawViewPrefs.widths) ? rawViewPrefs.widths : {};
  Object.entries(rawWidths).forEach(([rawKey, rawWidth]) => {
    const colValue = resolveColumnAlias(view, rawKey);
    if (!hasKnownColumn(view, colValue) || !isResizableColumn(view, colValue)) return;
    const clamped = clampColumnWidth(view, colValue, rawWidth);
    if (clamped === undefined) return;
    result.widths[colValue] = clamped;
  });

  const rawOrder = Array.isArray(rawViewPrefs.order) ? rawViewPrefs.order : [];
  const normalizedOrder = [];
  rawOrder.forEach(rawKey => {
    if (typeof rawKey !== "string") return;
    const colValue = resolveColumnAlias(view, rawKey);
    if (!hasKnownColumn(view, colValue)) return;
    if (!normalizedOrder.includes(colValue)) {
      normalizedOrder.push(colValue);
    }
  });
  defaults.order.forEach(colValue => {
    if (!normalizedOrder.includes(colValue)) {
      normalizedOrder.push(colValue);
    }
  });
  result.order = normalizedOrder;

  getBaseHeadersForView(view).forEach(header => {
    if (header.mandatory === true) {
      result.visibility[header.value] = true;
    }
  });

  // Hard enforcement for actions: always visible and fixed width.
  if (hasKnownColumn(view, "actions")) {
    result.visibility.actions = true;
    result.widths.actions = ACTIONS_FIXED_WIDTH;
  }

  return result;
};

const normalizeColumnPrefs = rawPrefs => {
  const migratedPrefs = migrateColumnPrefsToSupportedShape(rawPrefs);
  return {
    version: COLUMN_PREFS_VERSION,
    patients: normalizeViewPrefs("patients", migratedPrefs.patients),
    studies: normalizeViewPrefs("studies", migratedPrefs.studies)
  };
};

const getInitialState = () => ({
  groupIndex: 0,
  search: "",
  searchLogic: "and",
  searchFilters: initialFilter,
  searchMeta: getInitialSearchMeta(),
  selection: [],
  selectionFetchingCount: 0,
  uploadingData: [],
  isRefreshing: false,
  // Series display settings
  hideForProcessingSeries: true,
  hideNonImagingSeries: false,
  groupProjRawSeries: true,
  columnPrefs: normalizeColumnPrefs({}),
  demoColumnDefaultsActive: false,
  triageColorsEnabled: true,
  maskSensitiveData: false
});

let saveColumnPrefsDebounceTimer = null;
let saveColumnPrefsDebounceResolvers = [];

export default {
  namespaced: true,
  state: getInitialState(),
  getters: {
    group: state => dashboardGroups[state.groupIndex],
    groups: () => dashboardGroups,
    searchFilters: state => state.searchFilters,
    searchMeta: state => state.searchMeta,
    numberOfSelectedSeries: state =>
      state.selection.reduce((sum, d) => sum + d.series.length, 0),
    isSelectionFetching: state => state.selectionFetchingCount > 0,
    isUploading: state => {
      if (
        state.uploadingData &&
        state.uploadingData.length &&
        state.uploadingData.length > 0
      ) {
        return true;
      } else {
        return false;
      }
    },
    isRefreshing: state => state.isRefreshing,
    uploadingData: state => state.uploadingData,
    hideForProcessingSeries: state => state.hideForProcessingSeries,
    hideNonImagingSeries: state => state.hideNonImagingSeries,
    groupProjRawSeries: state => state.groupProjRawSeries,
    maskSensitiveData: state => state.maskSensitiveData,
    resolvedHeaders: state => view => {
      if (!isKnownView(view)) return [];

      const safePrefs = normalizeColumnPrefs(state.columnPrefs || {})[view];
      return getBaseHeadersForView(view)
        .filter(header => safePrefs.visibility[header.value] !== false)
        .map(header => {
          const normalizedHeader = { ...header };
          const width = safePrefs.widths[header.value];
          if (width !== undefined) {
            normalizedHeader.width = width;
          }
          return normalizedHeader;
        });
    }
  },

  mutations: {
    reset: state => {
      const s = getInitialState();
      Object.keys(s).forEach(key => (state[key] = s[key]));
    },
    resetAllFilters: state => (state.searchFilters = initialFilter),
    updateGroup: (state, index) => (state.groupIndex = index),
    updateSearch: (state, value) => (state.search = value),
    updateSearchLogic: (state, value) =>
      (state.searchLogic = value === "or" ? "or" : "and"),
    setSearchMeta: (state, value) => {
      state.searchMeta = {
        ...getInitialSearchMeta(),
        ...(value || {})
      };
    },
    resetSearchMeta: state => {
      state.searchMeta = getInitialSearchMeta();
    },
    updateSelection: (state, value) => (state.selection = value),
    adjustSelectionFetchingCount: (state, delta) => {
      state.selectionFetchingCount = Math.max(0, state.selectionFetchingCount + delta);
    },
    upateUploadingData: (state, value) => {
      state.uploadingData = [...state.uploadingData, ...value];
    },
    resetUploading: state => {
      state.uploadingData.splice(0);
    },
    setFilter: (state, { key, value }) => {
      state.searchFilters[key[0]][key[1]] = value;
    },
    resetFilter: (state, key) => {
      state.searchFilters[key[0]][key[1]] = "";
    },
    terminateUploading: (state, value) => {
      if (value) {
        if (
          state.uploadingData &&
          state.uploadingData.length &&
          state.uploadingData.length > 0
        ) {
          let uploadingData = state.uploadingData.filter(
            i => i.uniqueUID !== value
          );
          state.uploadingData = uploadingData;
        }
      }
    },
    setRefreshing: (state, isRefreshing) => (state.isRefreshing = isRefreshing),
    setHideForProcessingSeries: (state, value) =>
      (state.hideForProcessingSeries = value),
    setHideNonImagingSeries: (state, value) =>
      (state.hideNonImagingSeries = value),
    setGroupProjRawSeries: (state, value) => (state.groupProjRawSeries = value),
    setTriageColorsEnabled: (state, value) =>
      (state.triageColorsEnabled = Boolean(value)),
    setMaskSensitiveData: (state, value) =>
      (state.maskSensitiveData = Boolean(value)),
    SET_COLUMN_PREFS(state, prefs) {
      state.columnPrefs = normalizeColumnPrefs(prefs);
    },
    APPLY_DEMO_DEFAULT_COLUMN_PREFS(state) {
      state.demoColumnDefaultsActive = true;
      state.columnPrefs = normalizeColumnPrefs(getDemoDefaultRawPrefs());
    },
    RESET_COLUMN_PREFS(state) {
      state.columnPrefs = normalizeColumnPrefs(
        state.demoColumnDefaultsActive ? getDemoDefaultRawPrefs() : {}
      );
    },
    RESET_COLUMN_PREFS_FOR_VIEW(state, payload = {}) {
      const { view } = payload;
      if (!isKnownView(view)) return;
      const demoRawPrefs = state.demoColumnDefaultsActive
        ? getDemoDefaultRawPrefs()[view]
        : undefined;
      state.columnPrefs = {
        ...normalizeColumnPrefs(state.columnPrefs),
        [view]: normalizeViewPrefs(view, demoRawPrefs)
      };
    },
    SET_COLUMN_VISIBILITY(state, payload = {}) {
      const { view, colValue, visible } = payload;
      if (!isKnownView(view) || !hasKnownColumn(view, colValue)) return;

      if (colValue === "actions") {
        state.columnPrefs[view].visibility = {
          ...state.columnPrefs[view].visibility,
          [colValue]: true
        };
        return;
      }

      state.columnPrefs[view].visibility = {
        ...state.columnPrefs[view].visibility,
        [colValue]: isMandatoryColumn(view, colValue)
          ? true
          : Boolean(visible)
      };
    },
    SET_COLUMN_WIDTH(state, payload = {}) {
      const { view, colValue, width } = payload;
      if (!isKnownView(view) || !hasKnownColumn(view, colValue)) return;
      if (!isResizableColumn(view, colValue)) return;

      const clamped = clampColumnWidth(view, colValue, width);
      if (clamped === undefined) {
        const nextWidths = { ...state.columnPrefs[view].widths };
        delete nextWidths[colValue];
        state.columnPrefs[view].widths = nextWidths;
        return;
      }
      state.columnPrefs[view].widths = {
        ...state.columnPrefs[view].widths,
        [colValue]: clamped
      };
    }
  },
  actions: {
    async saveColumnPrefs({ state, rootState }) {
      // The shared demo account must not persist per-visitor column changes.
      if (rootState.auth.user?.is_demo) return;
      const normalizedPrefs = normalizeColumnPrefs(state.columnPrefs);
      await putUserSetting("dashboard_column_prefs", normalizedPrefs);
    },
    async saveTriageColorsEnabled({ state }) {
      await putUserSetting(
        "dashboard_triage_colors_enabled",
        state.triageColorsEnabled
      );
    },
    saveColumnPrefsDebounced({ dispatch }, delay = 500) {
      const waitMs = Math.max(0, Number(delay) || 500);

      return new Promise(resolve => {
        saveColumnPrefsDebounceResolvers.push(resolve);

        if (saveColumnPrefsDebounceTimer) {
          clearTimeout(saveColumnPrefsDebounceTimer);
        }

        saveColumnPrefsDebounceTimer = setTimeout(async () => {
          saveColumnPrefsDebounceTimer = null;
          const resolvers = [...saveColumnPrefsDebounceResolvers];
          saveColumnPrefsDebounceResolvers = [];

          try {
            await dispatch("saveColumnPrefs");
          } catch (err) {
            if (process.env.NODE_ENV !== "production") {
              console.warn("Failed to save dashboard_column_prefs", err);
            }
          } finally {
            resolvers.forEach(done => done());
          }
        }, waitMs);
      });
    }
  }
};
